import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { useBreadcrumb } from "shared/context/BreadcrumbContext";
import { Search, LayoutGrid, List, ChevronDown, Filter, Plus, Pin, MoreVertical, X } from "lucide-react";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";
import { primary as PRIMARY, primaryHover as PRIMARY_HOVER, primaryAlpha } from "shared/theme/trodoColors";
import { useBoards } from "features/analytics/studio/hooks/useBoards";
import { usePinnedBoards } from "features/analytics/studio/context/PinnedBoardsContext";
import * as analyticsApi from "features/analytics/studio/api/analyticsApi";
import Icon from "shared/components/Icon";
import { useAppDialog } from "shared/context/AppDialogContext";

// Toolbar pill — all chips on the reports toolbar share these dims so the
// whole row reads as one segmented control. Matches Sessions/Events/Use-cases.
const TOOLBAR_PILL_CLASS =
  "inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--border-default)] bg-surface px-3 text-[12px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover";

const BoardIcon = ({ color = "currentColor", size = 16 }) => <Icon name="dashboard" size={size} color={color} />;

/**
 * Colorful default board icon — a small rounded gradient tile used as the
 * fallback when a board has no emoji set. Warm brand-tone gradient with the
 * dashboard glyph inset in white.
 */
const DefaultBoardIcon = ({ size = 28 }) => (
  <div
    className="flex items-center justify-center rounded-md flex-shrink-0"
    style={{
      width: size,
      height: size,
      background: "linear-gradient(135deg, #ff6d29 0%, #f9a826 50%, #ff4d8d 100%)",
      boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.18)",
    }}
  >
    <Icon name="dashboard" size={Math.round(size * 0.55)} color="var(--surface-base)" />
  </div>
);

const InsightsIcon = ({ color = "currentColor", size = 16 }) => <Icon name="analytics" size={size} color={color} />;
const FunnelsIcon = ({ color = "currentColor", size = 16 }) => <Icon name="funnel" size={size} color={color} />;
const FlowsIcon = ({ color = "currentColor", size = 16 }) => <Icon name="flows" size={size} color={color} />;
const RetentionIcon = ({ color = "currentColor", size = 16 }) => <Icon name="retention" size={size} color={color} />;

const getInitialsFromCreator = (creatorName, creatorEmail) => {
  if (creatorName && typeof creatorName === "string") {
    const parts = creatorName.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    if (parts.length === 1 && parts[0].length >= 1) return parts[0].substring(0, 2).toUpperCase();
  }
  if (creatorEmail && typeof creatorEmail === "string") {
    const parts = creatorEmail.split("@")[0].split(".");
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return creatorEmail.substring(0, 2).toUpperCase();
  }
  return "?";
};

const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / (1000 * 60));
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} min${diffMin === 1 ? "" : "s"}`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr${diffHr === 1 ? "" : "s"}`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? "" : "s"}`;
  if (diffDays < 30) {
    const w = Math.floor(diffDays / 7);
    return `${w} week${w === 1 ? "" : "s"}`;
  }
  if (diffDays < 365) {
    const mo = Math.floor(diffDays / 30);
    return `${mo} month${mo === 1 ? "" : "s"}`;
  }
  const y = Math.floor(diffDays / 365);
  return `${y} year${y === 1 ? "" : "s"}`;
};

const TYPE_OPTIONS = [
  { id: "boards",    label: "Boards",    checked: true, icon: BoardIcon },
  { id: "insights",  label: "Insights",  icon: InsightsIcon },
  { id: "funnels",   label: "Funnels",   icon: FunnelsIcon },
  { id: "flows",     label: "Flows",     icon: FlowsIcon },
  { id: "retention", label: "Retention", icon: RetentionIcon },
];

function iconForReportType(typeLabel) {
  const map = { Insight: InsightsIcon, Funnel: FunnelsIcon, Flow: FlowsIcon, Retention: RetentionIcon };
  return map[typeLabel] || InsightsIcon;
}

function cardTypeToFilterId(cardType) {
  const t = (cardType || "").toLowerCase();
  if (t === "insights") return "insights";
  if (t === "funnel") return "funnels";
  if (t === "flow") return "flows";
  if (t === "retention") return "retention";
  return null;
}

function editorTypeFromCardType(cardType) {
  const t = (cardType || "insights").toLowerCase();
  if (t === "funnel") return "funnels";
  if (t === "flow") return "flows";
  if (t === "retention") return "retention";
  return t;
}

function getSelectedTeamId() {
  try {
    const raw = typeof window !== "undefined" ? localStorage.getItem("selectedTeamData") : null;
    if (!raw) return null;
    const data = JSON.parse(raw);
    const id = data?.id ?? data?._id;
    return id != null ? id : null;
  } catch { return null; }
}

export default function ReportsPage() {
  useBreadcrumb([{ label: 'Reports' }]);
  const navigate = useNavigate();
  const { team } = useParams();
  const { boards, loading, error, loadBoards, createBoard } = useBoards();
  const { togglePin, isPinned, pinnedBoardIds, ensureLoaded } = usePinnedBoards();
  // Reports is the only place pinned boards are rendered or pinned, so this is
  // where the server sync happens. Everywhere else reads localStorage only.
  useEffect(() => { ensureLoaded(); }, [ensureLoaded]);
  const dialog = useAppDialog();

  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("list");
  const [typesFilter, setTypesFilter] = useState(() => TYPE_OPTIONS.map((t) => ({ ...t, checked: t.id === "boards" })));
  const [typesDropdownOpen, setTypesDropdownOpen] = useState(false);
  const typesFilterDropdownRef = useRef(null);
  const [allCards, setAllCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(false);
  const teamId = getSelectedTeamId();

  // Create dropdown
  const [createOpen, setCreateOpen] = useState(false);
  const [createPos, setCreatePos] = useState(null);
  const createBtnRef = useRef(null);
  const createMenuRef = useRef(null);

  // Row 3-dot menu — { id, pos: { top, left } } or null
  const [rowMenu, setRowMenu] = useState(null);
  const rowMenuRef = useRef(null);

  useEffect(() => {
    if (!rowMenu) return;
    const close = (e) => {
      if (rowMenuRef.current?.contains(e.target)) return;
      // Allow click on the trigger button to toggle (its own handler closes)
      if (e.target.closest?.("[data-row-menu-trigger]")) return;
      setRowMenu(null);
    };
    const onKey = (e) => { if (e.key === "Escape") setRowMenu(null); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [rowMenu]);

  const openRowMenu = (e, itemId) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    const W = 160;
    setRowMenu({
      id: itemId,
      pos: { top: r.bottom + 4, left: Math.max(8, r.right - W) },
    });
  };

  const handleDeleteBoard = async (boardId) => {
    setRowMenu(null);
    const ok = await dialog.confirm("This board will be permanently removed. This action cannot be undone.", {
      title: "Delete this board?",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      danger: true,
    });
    if (!ok) return;
    try {
      await analyticsApi.deleteBoard(boardId, teamId);
      if (teamId != null) loadBoards({ teamId });
    } catch (err) {
      console.error("Failed to delete board:", err);
      await dialog.alert("Failed to delete board.", { title: "Delete failed", variant: "error" });
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (typesFilterDropdownRef.current && !typesFilterDropdownRef.current.contains(e.target)) {
        setTypesDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!createOpen) return;
    const close = (e) => {
      if (createMenuRef.current?.contains(e.target)) return;
      if (createBtnRef.current?.contains(e.target)) return;
      setCreateOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setCreateOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [createOpen]);

  useEffect(() => {
    if (teamId == null) return;
    loadBoards({ teamId });
  }, [loadBoards, teamId]);

  const loadAllCards = useCallback(async () => {
    if (!boards || boards.length === 0) { setAllCards([]); return; }
    setLoadingCards(true);
    try {
      const results = await Promise.all(
        boards.map(async (b) => {
          try {
            const res = await analyticsApi.fetchBoard(b.id, true, teamId);
            const boardData = res?.data || res;
            const cards = boardData?.cards || [];
            return (cards || []).map((card) => ({ card, boardId: b.id, boardName: b.name || "Untitled" }));
          } catch { return []; }
        })
      );
      setAllCards(results.flat());
    } catch (err) {
      console.error("Failed to load cards for reports filter:", err);
      setAllCards([]);
    } finally { setLoadingCards(false); }
  }, [boards, teamId]);

  useEffect(() => { loadAllCards(); }, [loadAllCards]);

  const checkedTypeIds = typesFilter.filter((t) => t.checked).map((t) => t.id);
  const searchLower = (search || "").toLowerCase().trim();

  const filteredItems = (() => {
    const items = [];
    if (checkedTypeIds.length === 0) return items;

    if (checkedTypeIds.includes("boards")) {
      boards
        .filter((board) => !searchLower || (board.name || "").toLowerCase().includes(searchLower))
        .forEach((board) => {
          items.push({
            id: board.id,
            title: board.name,
            type: "board",
            icon: board.icon || null,
            lastEdited: formatRelativeTime(board.updatedAt),
            creatorInitials: getInitialsFromCreator(board.creatorName, board.creatorEmail),
            creatorName: board.creatorName || board.creatorEmail || null,
            cardCount: board.cardCount || 0,
          });
        });
    }

    const reportTypeIds = ["insights", "funnels", "flows", "retention"].filter((id) => checkedTypeIds.includes(id));
    if (reportTypeIds.length > 0 && allCards.length >= 0) {
      allCards.forEach(({ card, boardId, boardName }) => {
        const filterId = cardTypeToFilterId(card?.cardType);
        if (!filterId || !reportTypeIds.includes(filterId)) return;
        const title = card?.title || "Untitled";
        if (searchLower && !title.toLowerCase().includes(searchLower) && !(boardName || "").toLowerCase().includes(searchLower)) return;
        const board = boards.find((b) => b.id === boardId);
        const creatorName = board ? (board.creatorName || board.creatorEmail || null) : null;
        items.push({
          id: `card-${boardId}-${card?.id}`,
          type: "report",
          title,
          lastEdited: card?.updatedAt ? formatRelativeTime(card.updatedAt) : "—",
          boardId,
          boardName,
          cardId: card?.id,
          cardType: card?.cardType,
          editorType: editorTypeFromCardType(card?.cardType),
          typeLabel: filterId === "funnels" ? "Funnel" : filterId === "flows" ? "Flow" : filterId === "retention" ? "Retention" : "Insight",
          creatorName,
        });
      });
    }
    return items;
  })();

  // Pinned boards strip (resolved from pinnedBoardIds + boards)
  const pinnedBoards = useMemo(() => {
    if (!boards || boards.length === 0 || pinnedBoardIds.length === 0) return [];
    return pinnedBoardIds
      .map((id) => boards.find((b) => String(b.id) === String(id)))
      .filter(Boolean);
  }, [boards, pinnedBoardIds]);

  const handleItemClick = (item) => {
    if (item.type === "board") {
      navigate(`/${team}/analytics/boards/${item.id}`);
    } else if (item.type === "report") {
      if (item.editorType === "flows") {
        const q = new URLSearchParams();
        if (item.cardId) q.set("cardId", item.cardId);
        const qs = q.toString();
        navigate(`/${team}/analytics/flow/${item.boardId}${qs ? `?${qs}` : ""}`);
      } else {
        const params = new URLSearchParams({ type: item.editorType });
        if (item.cardId) params.set("cardId", item.cardId);
        navigate(`/${team}/analytics/boards/${item.boardId}/editor?${params.toString()}`);
      }
    }
  };

  const toggleType = (id) => {
    setTypesFilter((prev) => prev.map((t) => (t.id === id ? { ...t, checked: !t.checked } : t)));
  };

  const checkedTypes = typesFilter.filter((t) => t.checked);
  const typesFilterSummary = (() => {
    if (checkedTypes.length === 0) return "Select types";
    if (checkedTypes.length === typesFilter.length) return "All types";
    return checkedTypes.map((t) => t.label).join(", ");
  })();

  const openCreateMenu = () => {
    if (!createBtnRef.current) return;
    const r = createBtnRef.current.getBoundingClientRect();
    const W = 200;
    setCreatePos({ top: r.bottom + 6, left: Math.max(8, r.right - W) });
    setCreateOpen((v) => !v);
  };

  const handleCreate = async (kind) => {
    setCreateOpen(false);
    if (kind === "insights") {
      navigate(`/${team}/analytics/boards/new/editor?type=insights`);
    } else if (kind === "funnels") {
      navigate(`/${team}/analytics/boards/new/editor?type=funnels`);
    } else if (kind === "flows") {
      navigate(`/${team}/analytics/flow/new`);
    } else if (kind === "retention") {
      navigate(`/${team}/analytics/boards/new/editor?type=retention`);
    } else if (kind === "board") {
      try {
        const newBoard = await createBoard({ name: "Untitled", description: null });
        if (newBoard?.id) navigate(`/${team}/analytics/boards/${newBoard.id}`);
      } catch (err) {
        console.error("Failed to create board:", err);
      }
    }
  };

  const createOptions = [
    { id: "insights",  label: "Insights",  Icon: InsightsIcon },
    { id: "funnels",   label: "Funnels",   Icon: FunnelsIcon },
    { id: "flows",     label: "Flows",     Icon: FlowsIcon },
    { id: "retention", label: "Retention", Icon: RetentionIcon },
    { id: "board",     label: "New board", Icon: BoardIcon },
  ];

  // Token-driven icon well — same brand tint in both themes via brand-primary
  // with a low alpha overlay (the surface-sunken token already provides the
  // contrast we want for the muted variant). Using rgba on the brand var keeps
  // dark mode readable without a separate dark-mode override.
  const iconWellStyle = {
    backgroundColor: "color-mix(in srgb, var(--brand-primary) 12%, transparent)",
    color: "var(--brand-primary)",
  };

  return (
    <div
      className="flex flex-1 flex-col bg-surface"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* ── Single sticky toolbar ────────────────────────────────────────
          Order: [count] [type filter] [grid|list toggle] [search ─────] [Create]
          All controls h-8, rounded-md, border-default. Matches Sessions/Events/Use-cases. */}
      <div className="sticky top-0 z-10 flex flex-col gap-2 bg-surface px-3 sm:px-4 pb-2 pt-3">
        <div className="flex flex-wrap items-center gap-2">

        {/* Count: left anchor */}
        <div className="inline-flex h-8 items-stretch overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
          <div className="flex items-center gap-1.5 px-3 text-[12px]">
            <span className="font-semibold tabular-nums text-[color:var(--text-primary)]">
              {filteredItems.length.toLocaleString()}
            </span>
            <span className="text-[color:var(--text-muted)]">
              {filteredItems.length === 1 ? "result" : "results"}
            </span>
          </div>
        </div>

        {/* Type filter */}
        <div className="relative shrink-0" ref={typesFilterDropdownRef}>
          <button
            type="button"
            onClick={() => setTypesDropdownOpen((o) => !o)}
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--border-default)] bg-surface px-3 text-[12px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover"
            aria-haspopup="listbox"
            aria-expanded={typesDropdownOpen}
          >
            <Filter size={13} className="shrink-0 text-[color:var(--text-muted)]" />
            <span className="max-w-[140px] truncate" title={typesFilterSummary}>{typesFilterSummary}</span>
            <ChevronDown
              size={13}
              className="shrink-0 text-[color:var(--text-muted)] transition-transform"
              style={{ transform: typesDropdownOpen ? "rotate(180deg)" : undefined }}
            />
          </button>
          {typesDropdownOpen && (
            <ul
              role="listbox"
              aria-multiselectable="true"
              className="absolute left-0 top-full z-30 mt-1 max-h-64 w-[200px] overflow-y-auto rounded-md border border-[color:var(--border-default)] bg-surface py-1 shadow-token-lg"
            >
              {typesFilter.map((t) => {
                const TypeIcon = t.icon;
                return (
                  <li key={t.id} role="none">
                    <label className="mx-1 my-0.5 flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-1.5 transition-colors hover:bg-surface-hover">
                      <input
                        type="checkbox"
                        checked={t.checked}
                        onChange={() => toggleType(t.id)}
                        className="h-3.5 w-3.5 shrink-0 cursor-pointer accent-[color:var(--brand-primary)]"
                      />
                      {TypeIcon && (
                        <TypeIcon size={13} color={t.checked ? "var(--text-primary)" : "var(--text-muted)"} />
                      )}
                      <span className="text-[12px]" style={{ color: t.checked ? "var(--text-primary)" : "var(--text-muted)" }}>
                        {t.label}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Grid / List toggle: icon + label like Use Cases List/Map */}
        <div
          className="inline-flex h-8 shrink-0 items-stretch overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface"
          role="group"
          aria-label="View layout"
        >
          <button
            type="button"
            onClick={() => setViewMode("grid")}
            className={`inline-flex items-center gap-1.5 px-3 text-[12px] font-medium transition-colors ${viewMode === "grid" ? "bg-surface-hover text-[color:var(--text-primary)]" : "text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"}`}
            aria-pressed={viewMode === "grid"}
          >
            <LayoutGrid size={13} />
            Grid
          </button>
          <button
            type="button"
            onClick={() => setViewMode("list")}
            className={`inline-flex items-center gap-1.5 border-l border-[color:var(--border-default)] px-3 text-[12px] font-medium transition-colors ${viewMode === "list" ? "bg-surface-hover text-[color:var(--text-primary)]" : "text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"}`}
            aria-pressed={viewMode === "list"}
          >
            <List size={13} />
            List
          </button>
        </div>

        {/* Search: fixed width like other toolbar searches */}
        <div className="flex h-8 w-[240px] shrink-0 items-center gap-2 rounded-md border border-[color:var(--border-default)] bg-surface px-3">
          <Search size={13} className="shrink-0 text-[color:var(--text-muted)]" />
          <input
            type="text"
            placeholder="Search reports"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="min-w-0 flex-1 bg-transparent text-[12px] text-[color:var(--text-primary)] outline-none placeholder:text-[color:var(--text-muted)]"
            style={{ fontFamily: "Inter, sans-serif" }}
          />
          {search ? (
            <button type="button" onClick={() => setSearch("")} className="shrink-0 text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] transition-colors" aria-label="Clear search">
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>

        {/* Spacer: pushes Create to the far right */}
        <div className="flex-1" />

        {/* Create: primary, rightmost */}
        <button
          ref={createBtnRef}
          type="button"
          onClick={openCreateMenu}
          className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md px-3 text-[12px] font-semibold text-ink-onBrand transition-colors"
          style={{ backgroundColor: createOpen ? "var(--brand-primary-hover)" : "var(--brand-primary)" }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--brand-primary-hover)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = createOpen ? "var(--brand-primary-hover)" : "var(--brand-primary)"; }}
        >
          <Plus size={14} strokeWidth={2.5} />
          Create
          <ChevronDown size={13} style={{ transition: "transform 0.15s", transform: createOpen ? "rotate(180deg)" : "none" }} />
        </button>
        </div>
      </div>

      {/* Row 3-dot menu: token-driven hover, proper shadow, rounded-md */}
      {rowMenu && typeof document !== "undefined" && createPortal(
        <div
          ref={rowMenuRef}
          className="fixed overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface py-1 shadow-token-lg"
          style={{ top: rowMenu.pos.top, left: rowMenu.pos.left, width: 180, zIndex: 99999, fontFamily: "Inter, sans-serif" }}
        >
          <button
            type="button"
            onClick={() => { togglePin(rowMenu.id); setRowMenu(null); }}
            className="block w-full px-3 py-2 text-left text-[12px] text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover"
          >
            {isPinned(rowMenu.id) ? "Unpin board" : "Pin board"}
          </button>
          <button
            type="button"
            onClick={() => handleDeleteBoard(rowMenu.id)}
            className="block w-full px-3 py-2 text-left text-[12px] text-[color:var(--feedback-error)] transition-colors hover:bg-[color:var(--feedback-error-bg)]"
          >
            Delete
          </button>
        </div>,
        document.body
      )}

      {createOpen && createPos && typeof document !== "undefined" && createPortal(
        <div
          ref={createMenuRef}
          className="fixed overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface shadow-token-lg"
          style={{ top: createPos.top, left: createPos.left, width: 220, zIndex: 99999, fontFamily: "Inter, sans-serif" }}
        >
          <div className="border-b border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">
            Create new
          </div>
          <div className="py-1">
            {createOptions.map((opt) => {
              const Icon = opt.Icon;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => handleCreate(opt.id)}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12px] text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover"
                >
                  <Icon size={14} color="var(--brand-primary)" />
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}

      {/* Page rides the AnalyticsLayout outer scroll (same model as Sessions /
          Events / Use Cases). No inner overflow-auto: content flows. */}
      <main>
        <div className="w-full min-w-0 px-4 pb-8 pt-3">
          {/* Pinned boards strip: chip-style cards with token hover */}
          {pinnedBoards.length > 0 && (
            <div className="mb-5">
              <div className="mb-2 flex items-center gap-1.5">
                <Pin size={12} className="text-[color:var(--text-muted)]" />
                <span className="text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">
                  Pinned boards
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {pinnedBoards.map((board) => (
                  <button
                    key={board.id}
                    type="button"
                    onClick={() => navigate(`/${team}/analytics/boards/${board.id}`)}
                    className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--border-default)] bg-surface px-3 text-[12px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover hover:border-[color:var(--brand-primary)]"
                  >
                    {board.icon && <span style={{ fontSize: 13, lineHeight: 1 }}>{board.icon}</span>}
                    <span className="max-w-[160px] truncate">{board.name || "Untitled"}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Toolbar moved to the sticky header: no second toolbar row here */}

          {/* ── Results (empty state handled in the branch below) ─────────── */}
          {loading || loadingCards ? (
            <div className="py-10 text-center text-[12px] text-[color:var(--text-muted)]">
              {loading ? "Loading boards…" : "Loading reports…"}
            </div>
          ) : error ? (
            <div className="rounded-md border border-[color:var(--border-default)] bg-[color:var(--feedback-error-bg)] px-4 py-3 text-[12px] text-[color:var(--feedback-error)]">
              {error}
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="rounded-md border border-[color:var(--border-default)] bg-surface px-6 py-10 text-center">
              <p className="text-[13px] font-medium text-[color:var(--text-primary)]">
                {checkedTypeIds.length === 0 ? "Select at least one type" : "No results match"}
              </p>
              <p className="mt-1 text-[12px] text-[color:var(--text-muted)]">
                {checkedTypeIds.length === 0
                  ? "Open the type filter and select Boards, Insights, Funnels, Flows, or Retention."
                  : "Try changing your search or type filter."}
              </p>
            </div>
          ) : viewMode === "grid" ? (
            /* ── Grid view ── */
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filteredItems.map((item) => {
                const isReport = item.type === "report";
                const TypeIcon = isReport ? iconForReportType(item.typeLabel) : null;
                return (
                  <div
                    key={item.id}
                    className="group relative flex flex-col rounded-md border border-[color:var(--border-default)] bg-surface transition-colors hover:border-[color:var(--brand-primary)] hover:bg-surface-hover"
                  >
                    {/* Card body: clickable */}
                    <button
                      type="button"
                      onClick={() => handleItemClick(item)}
                      className="flex flex-1 flex-col gap-2 p-3 text-left"
                    >
                      {/* Icon */}
                      {isReport ? (
                        <div className="flex h-8 w-8 items-center justify-center rounded-md" style={iconWellStyle}>
                          <TypeIcon size={15} color="var(--brand-primary)" />
                        </div>
                      ) : item.icon ? (
                        <div className="flex h-8 w-8 items-center justify-center" style={{ fontSize: 20, lineHeight: 1 }}>
                          {item.icon}
                        </div>
                      ) : (
                        <DefaultBoardIcon size={32} />
                      )}

                      {/* Title */}
                      <div className="truncate text-[13px] font-medium text-[color:var(--text-primary)]">
                        {item.title}
                      </div>

                      {/* Meta */}
                      <div className="flex flex-col gap-0.5">
                        {isReport ? (
                          <span className="truncate text-[11px] text-[color:var(--text-muted)]">
                            {item.typeLabel} · {item.boardName}
                          </span>
                        ) : (
                          <span className="text-[11px] text-[color:var(--text-muted)]">
                            {item.cardCount} card{item.cardCount !== 1 ? "s" : ""}
                          </span>
                        )}
                        <span className="text-[11px] tabular-nums text-[color:var(--text-muted)]">
                          {item.lastEdited}
                        </span>
                      </div>
                    </button>

                    {/* 3-dot: boards only, top-right corner */}
                    {!isReport && (
                      <button
                        type="button"
                        data-row-menu-trigger
                        onClick={(e) => openRowMenu(e, item.id)}
                        className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded text-[color:var(--text-muted)] opacity-0 transition-opacity group-hover:opacity-100 hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
                        aria-label="More options"
                      >
                        <MoreVertical size={13} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            /* ── List view — table-style card matching ClustersTable chrome ── */
            <div className="overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
              {/* Header row */}
              <div className="grid h-8 grid-cols-[1fr_100px_100px_28px] items-center gap-x-3 border-b border-[color:var(--border-default)] px-3 text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap text-[color:var(--text-muted)]">
                <span>Name</span>
                <span className="text-right">Type / cards</span>
                <span className="text-right">Last updated</span>
                <span />
              </div>

              {/* Rows */}
              <div>
                {filteredItems.map((item) => {
                  const isReport = item.type === "report";
                  const TypeIcon = isReport ? iconForReportType(item.typeLabel) : null;
                  return (
                    <div
                      key={item.id}
                      className="group grid h-8 grid-cols-[1fr_100px_100px_28px] items-center gap-x-3 border-b border-[color:var(--border-subtle)] px-3 text-[12px] text-[color:var(--text-primary)] last:border-b-0 transition-colors hover:bg-surface-hover"
                    >
                      {/* Name + icon */}
                      <button
                        type="button"
                        onClick={() => handleItemClick(item)}
                        className="flex h-full min-w-0 items-center gap-2 text-left"
                      >
                        {isReport ? (
                          <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded" style={iconWellStyle}>
                            <TypeIcon size={12} color="var(--brand-primary)" />
                          </div>
                        ) : item.icon ? (
                          <div className="flex h-5 w-5 shrink-0 items-center justify-center" style={{ fontSize: 13, lineHeight: 1 }}>
                            {item.icon}
                          </div>
                        ) : (
                          <DefaultBoardIcon size={20} />
                        )}
                        <span className="min-w-0 truncate">
                          <span className="font-medium text-[color:var(--text-primary)]">{item.title}</span>
                          {isReport && (
                            <span className="ml-1.5 text-[color:var(--text-muted)]">{item.boardName}</span>
                          )}
                        </span>
                      </button>

                      {/* Type / card count */}
                      <div className="truncate text-right tabular-nums text-[color:var(--text-muted)]">
                        {isReport
                          ? item.typeLabel
                          : `${item.cardCount} card${item.cardCount !== 1 ? "s" : ""}`}
                      </div>

                      {/* Last updated */}
                      <div className="truncate text-right tabular-nums text-[color:var(--text-muted)]">
                        {item.lastEdited}
                      </div>

                      {/* 3-dot: boards only */}
                      <div className="flex items-center justify-end">
                        {!isReport && (
                          <button
                            type="button"
                            data-row-menu-trigger
                            onClick={(e) => openRowMenu(e, item.id)}
                            className="flex h-6 w-6 items-center justify-center rounded text-[color:var(--text-muted)] opacity-0 transition-opacity group-hover:opacity-100 hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
                            aria-label="More options"
                          >
                            <MoreVertical size={13} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
